/**
 * Course Analytics API Client
 * 
 * 🎓 TICKET GHTVN9 - Métricas por curso + Panel de analítica (MVP)
 * 
 * Cliente API para obtener analytics y registrar eventos de aprendizaje.
 */

import apiClient from '../services/apiClient';

/**
 * Registrar evento de actividad (start/complete)
 * @param {string} courseId - ID del curso
 * @param {Object} eventData - Datos del evento
 * @param {string} eventData.activityId - ID de la actividad
 * @param {'resource'|'vrt_simulation'|'epl_lab'} eventData.activityType - Tipo de actividad
 * @param {'start'|'complete'} eventData.eventType - Tipo de evento
 * @param {number} [eventData.durationSeconds=0] - Duración en segundos (opcional)
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export async function postActivityEvent(courseId, eventData) {
  try {
    const { activityId, activityType, eventType, durationSeconds = 0 } = eventData;
    
    const response = await apiClient.post(`/courses/${courseId}/events`, {
      activityId,
      activityType,
      eventType,
      durationSeconds
    });
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('[CourseAnalyticsAPI] Error posting activity event:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
}

/**
 * Obtener métricas agregadas del curso
 * @param {string} courseId - ID del curso
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 * 
 * data shape:
 * {
 *   courseId: string,
 *   summary: {
 *     completedActivities: number,
 *     totalActivities: number,
 *     completionPercent: number,
 *     totalTimeSeconds: number,
 *     vrtSessionsCompleted: number,
 *     eplLabsCompleted: number,
 *     resourcesCompleted: number
 *   },
 *   modules: [{
 *     moduleId: string,
 *     moduleTitle: string,
 *     completedActivities: number,
 *     totalActivities: number,
 *     completionPercent: number,
 *     timeSeconds: number
 *   }],
 *   recentActivity: [{
 *     timestamp: Date,
 *     activityId: string,
 *     activityType: string,
 *     eventType: string,
 *     durationSeconds: number
 *   }]
 * }
 */
export async function fetchCourseAnalytics(courseId) {
  try {
    const response = await apiClient.get(`/courses/${courseId}/analytics`);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('[CourseAnalyticsAPI] Error fetching analytics:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
}

/**
 * Helper: Registrar inicio de actividad
 * @param {string} courseId - ID del curso
 * @param {string} activityId - ID de la actividad
 * @param {'resource'|'vrt_simulation'|'epl_lab'} activityType - Tipo de actividad
 * @returns {Promise<{success: boolean}>}
 */
export async function recordActivityStart(courseId, activityId, activityType) {
  return postActivityEvent(courseId, {
    activityId,
    activityType,
    eventType: 'start',
    durationSeconds: 0
  });
}

/**
 * Helper: Registrar completado de actividad
 * @param {string} courseId - ID del curso
 * @param {string} activityId - ID de la actividad
 * @param {'resource'|'vrt_simulation'|'epl_lab'} activityType - Tipo de actividad
 * @param {number} [durationSeconds=0] - Duración en segundos
 * @returns {Promise<{success: boolean}>}
 */
export async function recordActivityComplete(courseId, activityId, activityType, durationSeconds = 0) {
  return postActivityEvent(courseId, {
    activityId,
    activityType,
    eventType: 'complete',
    durationSeconds
  });
}
